import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import QuestionCard from "@/components/QuestionCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bookmark, NotebookPen, Sparkles, ArrowRight, MessageCircleQuestion, BookOpen, Highlighter, BookMarked, PenLine, Settings } from "lucide-react";
import { WelcomeStrip } from "@/components/account/WelcomeStrip";
import { BlogPostsTab } from "@/components/account/BlogPostsTab";
import { colorOf } from "@/lib/highlights";
import { useCommentaryBookmarks } from "@/components/commentary/useCommentaryBookmarks";
import { SavedCommentaryCard } from "@/components/commentary/SavedCommentaryCard";
import { timeAgo } from "@/lib/format";
import Seo from "@/components/Seo";

function EmptyState({ icon: Icon, title, body, cta }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-20 text-center">
      <Icon className="h-10 w-10 text-muted-foreground" />
      <p className="mt-4 font-serif text-2xl text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      {cta}
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, openAuth, favorites } = useAuth();
  const location = useLocation();
  const { data: overview } = useQuery({ queryKey: ["account-overview", favorites.length], queryFn: async () => (await api.get("/account/overview")).data, enabled: !!user });

  const cbm = useCommentaryBookmarks();
  const { data: hlData } = useQuery({ queryKey: ["highlights", "dash"], queryFn: async () => (await api.get("/highlights", { params: { sort: "newest" } })).data, enabled: !!user });
  const { data: favData } = useQuery({
    queryKey: ["dash-favorites", favorites.length],
    queryFn: async () => (await api.get("/favorites")).data,
    enabled: !!user,
  });
  const { data: journalData } = useQuery({
    queryKey: ["journal", "", null],
    queryFn: async () => (await api.get("/journal")).data,
    enabled: !!user,
  });

  if (loading) {
    return <div className="mx-auto max-w-5xl px-4 py-20"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Your study</span>
        <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] text-foreground sm:text-5xl">
          Sign in to keep your questions close
        </h1>
        <p className="mx-auto mt-4 max-w-md text-muted-foreground">
          Save the questions that move you and keep a private journal that grows with your faith.
        </p>
        <Button data-testid="dashboard-signin-button" onClick={openAuth} className="mt-8 rounded-full bg-primary px-8 py-6 text-base text-primary-foreground hover:bg-primary/90">
          Sign in or create an account
        </Button>
      </div>
    );
  }

  const savedQuestions = favData?.questions || [];
  const entries = journalData?.entries || [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <Seo title="Dashboard" />
      <WelcomeStrip user={{ ...user, created_at: overview?.user?.created_at }} counts={overview?.counts || { journal: entries.length, highlights: hlData?.total ?? 0, favorites: savedQuestions.length, commentary_bookmarks: cbm.bookmarks.length, posts: 0 }} />

      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-foreground">Your activity</h2>
        <Link to="/account" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="dashboard-account-link"><Settings className="h-3.5 w-3.5" /> Account settings</Link>
      </div>
      <Tabs defaultValue={location.hash === "#saved" ? "saved" : "journal"}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="journal" data-testid="tab-journal">
            <NotebookPen className="mr-2 h-4 w-4" /> Journal
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved">
            <Bookmark className="mr-2 h-4 w-4" /> Saved
          </TabsTrigger>
          <TabsTrigger value="highlights" data-testid="tab-highlights">
            <Highlighter className="mr-2 h-4 w-4" /> Highlights
          </TabsTrigger>
          <TabsTrigger value="blog" data-testid="tab-blog">
            <PenLine className="mr-2 h-4 w-4" /> Blog
          </TabsTrigger>
        </TabsList>

        <TabsContent value="blog"><BlogPostsTab /></TabsContent>

        <TabsContent value="highlights">
          {!hlData || hlData.total === 0 ? (
            <EmptyState
              icon={Highlighter}
              title="No highlights yet"
              body="Select any text in the Bible Reader or a commentary and pick a color. Labels and notes help you find them again."
              cta={
                <Link to="/read">
                  <Button className="mt-6 rounded-full bg-primary text-primary-foreground" data-testid="dashboard-start-highlighting">
                    Open the Bible Reader <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              }
            />
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Your most recent highlights</p>
                <Link to="/highlights" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="dashboard-open-highlights">All highlights, filters & PDF <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
              {hlData.highlights.slice(0, 6).map((h) => (
                <Link key={h.id} to={h.href} data-testid={`dash-highlight-${h.id}`} className="block rounded-2xl border border-border bg-card p-4 transition-all hover:border-gold hover:shadow-md" style={{ borderLeft: `4px solid ${colorOf(h.color).dot}` }}>
                  <p className="no-reftag flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gold">{h.source === "bible" ? <BookOpen className="h-3 w-3" /> : <BookMarked className="h-3 w-3" />} {h.reference} · {h.source_name}{h.label ? ` · ${h.label}` : ""}</p>
                  <p className="mt-2 line-clamp-2 font-serif text-[15px] leading-snug text-foreground">“{h.text}”</p>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="journal">
          {entries.length === 0 ? (
            <EmptyState
              icon={NotebookPen}
              title="Your journal is empty"
              body="Write a prayer, a reflection, or a note. Type @ to link any scripture or question."
              cta={
                <Link to="/journal/new">
                  <Button className="mt-6 rounded-full bg-primary text-primary-foreground" data-testid="dashboard-start-journal">
                    Start writing <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              }
            />
          ) : (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Link to="/journal" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="dashboard-open-journal">Open full journal <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
              {entries.slice(0, 8).map((e) => (
                <Link key={e.id} to={`/journal/${e.id}`} data-testid={`dash-entry-${e.id}`} className="block rounded-2xl border border-border bg-card p-5 transition-all hover:border-gold hover:shadow-md">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-serif text-xl text-foreground">{e.title || (e.question ? e.question.reference : "Untitled entry")}</p>
                    <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{timeAgo(e.updated_at)}</span>
                  </div>
                  {e.question && <p className="no-reftag mt-1 font-serif text-sm italic text-muted-foreground">“{e.question.question_kjv}”</p>}
                  {e.excerpt && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{e.excerpt}</p>}
                  {e.mentions?.length > 0 && (
                    <p className="mt-2 flex flex-wrap gap-1">
                      {e.mentions.slice(0, 4).map((m, i) => (
                        <span key={`${m.kind}-${m.label}-${i}`} className={`no-reftag inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[10px] ${m.kind === "question" ? "bg-primary/10 text-primary" : "bg-gold/15 text-gold"}`}>
                          {m.kind === "question" ? <MessageCircleQuestion className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}{m.label}
                        </span>
                      ))}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved">
          {cbm.bookmarks.length > 0 && (
            <section className="mb-8" data-testid="saved-commentary">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><BookMarked className="h-4 w-4 text-gold" /> Saved commentary</h2>
                <span className="font-mono text-xs text-muted-foreground">{cbm.bookmarks.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {cbm.bookmarks.map((b) => (
                  <SavedCommentaryCard key={b.id} b={b} saving={cbm.noteSaving} onRemove={() => cbm.toggle(b.commentary, b.section_id)} onNote={(n) => cbm.setNote(b.commentary, b.section_id, n)} />
                ))}
              </div>
              {savedQuestions.length > 0 && <h2 className="mt-8 flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><Bookmark className="h-4 w-4 text-gold" /> Saved questions</h2>}
            </section>
          )}
          {savedQuestions.length === 0 && cbm.bookmarks.length > 0 ? null : savedQuestions.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No saved questions yet"
              body="Tap the bookmark on any question or commentary section to keep it here."
              cta={
                <Link to="/explore">
                  <Button className="mt-6 rounded-full bg-primary text-primary-foreground">
                    Explore questions <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {savedQuestions.map((q, i) => (
                <QuestionCard key={q.id} q={q} index={i} />
              ))}
            </div>
          )}
        </TabsContent>

      </Tabs>

    </div>
  );
}
